import {
	Component, Input, Output, EventEmitter, OnChanges, SimpleChanges,
	ElementRef, ChangeDetectorRef
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators, ValidatorFn } from '@angular/forms';
import { ComponentBase, Changeable } from "common";
import { DeviceFilter, IncludeScope, Network, Device } from 'DiscoveryEngine.TS/Core';
import { List } from 'DiscoveryEngine.TS/Utils';
import { ViewerDeviceFilterDto } from "UvExplorerServer.Dtos.TS/Viewer";
import { DisplayDeviceListDto } from "UvExplorerServer.Dtos.TS/Session";
import { DiscResultsService } from 'discovery';
import { IdentityService } from 'identity';
import { MessageService } from 'base';
import { NavigationService } from 'netview';


@Component({
    selector: 'device-filter',
	templateUrl: './device-filter.component.html',
	styleUrls: ['./device-filter.component.scss']
})
export class DeviceFilterComponent extends ComponentBase implements Changeable {

	@Input()
	public target: ViewerDeviceFilterDto;

	@Output()
	public onApply: EventEmitter<any>;

	public form: UntypedFormGroup;

	public name: UntypedFormControl;
	public baseIncludeScope: UntypedFormControl;
	public nameFilters: UntypedFormControl;
	public ipScopeFilters: UntypedFormControl;
	public vlanFilters: UntypedFormControl;
	public oidFilters: UntypedFormControl;
	public deviceCategories: string[];
	public filterSummary: string;

	public previewDevices: DisplayDeviceListDto;
	public previewVisible: boolean;

	constructor(private fb: UntypedFormBuilder,
		private discResultsService: DiscResultsService,
		private navService: NavigationService,
		private identityService: IdentityService,
		private messageService: MessageService,
		private changeDetector: ChangeDetectorRef) {

		super();

		this.target = null;
		this.onApply = new EventEmitter<any>();

		this.filterSummary = "";

		this.buildForm();

		this.previewDevices = null;
		this.previewVisible = false;
	}

	protected buildForm(): void {
		let name = "";
		let baseIncludeScope = "AllDevices";
		let nameFilters = "";
		let ipScopeFilters = "";
		let vlanFilters = "";
		let oidFilters = "";
		let deviceCategories = [];

		if (this.target) {
			name = this.target.DeviceFilter.Name;

			baseIncludeScope = "AllDevices";
			switch (this.target.DeviceFilter.BaseIncludeScope) {
				case IncludeScope.AllNetworkDevices:
					baseIncludeScope = "AllNetworkDevices";
					break;
				case IncludeScope.AllSnmpDevices:
					baseIncludeScope = "AllSnmpDevices";
					break;
			}

			nameFilters = DeviceFilterComponent.stringListToInputText(this.target.DeviceFilter.NameFilters);
			ipScopeFilters = DeviceFilterComponent.stringListToInputText(this.target.DeviceFilter.IpScopeFilters);
			vlanFilters = DeviceFilterComponent.stringListToInputText(this.target.DeviceFilter.VlanFilters);
			oidFilters = DeviceFilterComponent.stringListToInputText(this.target.DeviceFilter.OidFilters);

			this.target.DeviceFilter.CategoryFilters.ForEach((catName: string) => {
				deviceCategories.push(catName);
			}, this);
		}

		this.name = new UntypedFormControl(name, Validators.required);
		this.baseIncludeScope = new UntypedFormControl(baseIncludeScope, Validators.required);
		this.nameFilters = new UntypedFormControl(nameFilters, this.validateFilters((filter: DeviceFilter, line: string) => filter.AddNameFilter(line), "validNameFilters"));
		this.ipScopeFilters = new UntypedFormControl(ipScopeFilters, this.validateFilters((filter: DeviceFilter, line: string) => filter.AddIpScopeFilter(line), "validIpScopeFilters"));
		this.vlanFilters = new UntypedFormControl(vlanFilters, this.validateFilters((filter: DeviceFilter, line: string) => filter.AddVlanFilter(line), "validVlanFilters"));
		this.oidFilters = new UntypedFormControl(oidFilters, this.validateFilters((filter: DeviceFilter, line: string) => filter.AddOidFilter(line), "validOidFilters"));
		this.deviceCategories = deviceCategories;

		this.form = this.fb.group({
			name: this.name,
			baseIncludeScope: this.baseIncludeScope,
			nameFilters: this.nameFilters,
			ipScopeFilters: this.ipScopeFilters,
			vlanFilters: this.vlanFilters,
			oidFilters: this.oidFilters,
		});

		this.updateFilterSummary();
	}

	public onCategoriesChange(): void {
		this.form.markAsDirty();
		this.updateFilterSummary();
	}

	private static stringListToInputText(values: List<string>): string {
		let result = "";

		values.ForEach((v: string) => {
			if (result.length > 0) {
				result += "\n";
			}
			result += v;
		}, this);

		return result;
	}

	private static inputTextToStringList(inputText: string): List<string> {
		let result = new List<string>();

		let arrayOfLines = inputText.match(/[^\r\n]+/g);
		if (arrayOfLines) {
			arrayOfLines.forEach((line: string) => {
				result.Add(line);
			}, this);
		}

		return result;
	}

	public updateFilterSummary(): void {
		let tmpFilter = new DeviceFilter();
		this.getChanges(tmpFilter);
		this.filterSummary = tmpFilter.Summary;
	}

	public ngOnChanges(changes: SimpleChanges): void {
		this.buildForm();
	}

	public get tempChanges(): boolean {
		return this.form.dirty;
	}

	public get tempChangesValid(): boolean {
		return this.form.valid;
	}

	public discardTempChanges(): void {
		this.buildForm();
	}

	public get invalidTempChanges(): string[] {
		return ["Device Filter"];
	}

	private validateFilters(addFunc: (DeviceFilter, string) => boolean, key: string): ValidatorFn {
		let lastValue: any = undefined;
		let lastResult: any = undefined;
		return (c: UntypedFormControl) => {
			let curValue: any = c.value;

			if (lastValue !== undefined) {
				if (curValue === lastValue) {
					return lastResult;
				}
			}

			let curResult: any = null;
			if (curValue) {
				let tmpFilter = new DeviceFilter();
				let lines = DeviceFilterComponent.inputTextToStringList(curValue);
				for (let i = 0; i < lines.Count; ++i) {
					let line = lines.Get(i);
					if (!addFunc(tmpFilter, line)) {
						curResult = {};
						curResult[key] = { valid: false };
						break;
					}
				}
			}
			
			lastValue = curValue;
			lastResult = curResult;

			return curResult;
		};
	}

	public applyTempChanges(): void {
		if (this.target) {
			this.getChanges(this.target.DeviceFilter);
			this.buildForm();
			this.onApply.emit();
		}
	}

	private getChanges(filter: DeviceFilter): void {
		filter.Clear();

		filter.Name = this.name.value;

		filter.BaseIncludeScope = IncludeScope.AllDevices;
		switch (this.baseIncludeScope.value) {
			case "AllNetworkDevices":
				filter.BaseIncludeScope = IncludeScope.AllNetworkDevices;
				break;
			case "AllSnmpDevices":
				filter.BaseIncludeScope = IncludeScope.AllSnmpDevices;
				break;
		}

		this.getFilters(this.nameFilters, (line: string) => filter.AddNameFilter(line));
		this.getFilters(this.ipScopeFilters, (line: string) => filter.AddIpScopeFilter(line));
		this.getFilters(this.vlanFilters, (line: string) => filter.AddVlanFilter(line));
		this.getFilters(this.oidFilters, (line: string) => filter.AddOidFilter(line));

		this.deviceCategories.forEach((catName: string) => {
			filter.AddCategoryFilterByName(catName);
		}, this);
	}

	private getFilters(c: UntypedFormControl, addFunc: (string) => boolean): void {
		let lines = DeviceFilterComponent.inputTextToStringList(c.value);
		lines.ForEach((line: string) => {
			addFunc(line);
		}, this);
	}

	public onPreview(): void {
		let tmpFilter = new DeviceFilter();
		this.getChanges(tmpFilter);

		this.previewDevices = null;
		let sessionGuid = this.identityService.sessionGuid;
		let netViewGuid = this.navService.location.netViewGuid;
		let agentGuid = this.navService.location.agentGuid;
		let schedDiscGuid = this.navService.location.schedDiscGuid;
		let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;
		this.discResultsService.getFilteredDeviceList(sessionGuid, netViewGuid, agentGuid,
			schedDiscGuid, schedDiscRsltGuid, "network", tmpFilter, null, false, false, false, false, false).subscribe(
			(result: DisplayDeviceListDto) => {
				this.previewDevices = result;
				this.previewVisible = true;
				this.changeDetector.detectChanges();
			},
			(error?) => {
				this.messageService.displayErrorMessage("Could not retrieve filtered device list");
			});
	}

	public onPreviewVisibleChange(visible: boolean): void {
		if (!visible) {
			this.previewDevices = null;
		}
	}

}
